import queryString from 'query-string';

import { MirrorService } from 'services/MirrorService';

import { isFirefox, isOpera } from 'helpers/browser';
import { getLocalStorageItem, setLocalStorageItem } from 'helpers/localStorage';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from 'helpers/sessionStorage';
import { assertString } from 'helpers/utils/checkType';

const PAGES_TO_REQUEST_PERMISSIONS_ON_STARTUP_FOR_UNREGISTERED =
  /^\/landing\/lp(9|10|11|12|13|14|15|16|17|18|19|20|21|22|23|24|25|26|27|28|29|30|31|32|33|34|35|36|37|38|39|40|41|42|43|44|45|46|47|48|49|50|51|52|53|54|55|56|57|58|59|60|62|63|64|65|66|68|69|70|71|72|73|74|75|76|77|78|80|81)\/?(1)?$/;

const NO_PUSH_PARTNERS_ID = ['3042', '3043', '3044', '3108', '3109', '1', '20'];

const NO_PUSH_PARTNERS_ID_LOCAL_STORAGE_KEY = 'web_push_disabled';

const PUSH_PERMISSIONS_ASKED_KEY = 'push_asked';

type RequestPushPermissionsParams = {
  onGranted: () => void;
  onDenied: () => void;
  onDefault: () => void;
};

export type WebpushApiData = {
  indexedDB: boolean;
  cookieEnabled: boolean;
  serviceWorker: boolean;
  pushManager: boolean;
  notification: boolean;
  defaultPermission: boolean;
  permission?: NotificationPermission;
};

/**
 * Checks is all needed api for firebase-sdk supported
 */
export const isAllNeededApiSupported = () =>
  'indexedDB' in window &&
  indexedDB !== null &&
  navigator.cookieEnabled &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window &&
  'fetch' in window;

export const requestPushPermissions = async ({
  onGranted,
  onDenied,
  onDefault,
}: RequestPushPermissionsParams) => {
  try {
    const status = await Notification.requestPermission();

    if (status === 'granted') {
      onGranted();
    } else if (status === 'default') {
      onDefault();
    } else if (status === 'denied') {
      onDenied();
    }
  } catch (err) {
    console.error('[requestPushPermissions]: ', err);
  }
};

export const isNotificationsPermissionsGranted = () =>
  'Notification' in window && window.Notification.permission === 'granted';

export const isNotificationsPermissionsDefault = () =>
  'Notification' in window && window.Notification.permission === 'default';

export const getWebpushApiData = (): WebpushApiData => ({
  indexedDB: !!('indexedDB' in window) && !!indexedDB,
  cookieEnabled: !!navigator.cookieEnabled,
  serviceWorker: !!('serviceWorker' in navigator),
  pushManager: !!('PushManager' in window),
  notification: !!('Notification' in window),
  defaultPermission: isNotificationsPermissionsDefault(),
  permission: window?.Notification?.permission,
});

export const isWebPushesEnabledBySite = () =>
  !MirrorService.isMidsOrDatesMe &&
  !MirrorService.isSimpleDates &&
  !MirrorService.isGoogleCertificationsGroup;

export const isWebPushesEnabledByPartners = (search: string) => {
  const { partner_id } = queryString.parse(search);

  if (
    (assertString(partner_id) && NO_PUSH_PARTNERS_ID.includes(partner_id)) ||
    getLocalStorageItem(NO_PUSH_PARTNERS_ID_LOCAL_STORAGE_KEY)
  ) {
    setLocalStorageItem(NO_PUSH_PARTNERS_ID_LOCAL_STORAGE_KEY, '1');

    return false;
  }

  return true;
};

export const shouldRequestPermissions = () =>
  isAllNeededApiSupported() && isNotificationsPermissionsDefault();

export const shouldRequestPermissionOnStartup = (pathname: string) =>
  !isFirefox() &&
  !isOpera() &&
  PAGES_TO_REQUEST_PERMISSIONS_ON_STARTUP_FOR_UNREGISTERED.test(pathname);

export const setPushPermissionsAskedFlag = () =>
  setSessionStorageItem(PUSH_PERMISSIONS_ASKED_KEY, '1');

export const isPushPermissionsAsked = () =>
  Boolean(getSessionStorageItem(PUSH_PERMISSIONS_ASKED_KEY));
